export interface Department {
  name: string;
  constraint: string;
  pain: string[];
  solution: string;
}

export interface CompPackage {
  structure: string;
  philosophy: string;
  paymentTrigger: string;
  criticalAction: string;
}

export interface Stage {
  number: number;
  name: string;
  headcount: string;
  role: string;
  leadershipStructure: string;
  qtyCompanies: string;
  departments: Department[];
  compPackage: CompPackage;
}

export const DEPARTMENT_NAMES = [
  'Product',
  'Marketing',
  'Sales',
  'Customer Service',
  'IT',
  'Recruiting',
  'Human Resources',
  'Finance',
  'Bottom Line',
] as const;

export const stages: Stage[] = [
  // ─── Stage 0: Improvise ───
  {
    number: 0,
    name: 'Improvise',
    headcount: 'NA',
    role: 'Researcher',
    leadershipStructure: 'Just You',
    qtyCompanies: "You're not a company yet",
    departments: [
      {
        name: 'Product',
        constraint: 'You have nothing to sell',
        pain: [
          'Ideas remain conceptual without tangible form',
          'Perfectionism preventing release of anything',
          'Fear of rejection or criticism of your creation',
          'Difficulty translating expertise into sellable offering',
        ],
        solution: 'Focus on creating something FREE to give away and gather feedback',
      },
      {
        name: 'Marketing',
        constraint: 'No one knows about your stuff',
        pain: [
          'No clear messaging about what you do',
          'Fear of self-promotion holding you back',
          'Reluctance to "put yourself out there"',
          'No audience built to receive your offers',
        ],
        solution: 'Tell people you have something to offer; sign up for free ad spend credits',
      },
      {
        name: 'Sales',
        constraint: "You don't know how to sell",
        pain: [
          'No consistent way to describe what you offer',
          'Negotiations feel awkward and unstructured',
          'Unable to handle objections effectively',
          'Giving away too much value for free',
        ],
        solution: 'Get people to try it for free',
      },
      {
        name: 'Customer Service',
        constraint: 'No customers to service',
        pain: [
          'Lack of real user experience',
          'No opportunity to demonstrate service quality',
          'No testimonials or case studies',
          'No feedback loop for improvement',
        ],
        solution: 'Service your free customers',
      },
      {
        name: 'IT',
        constraint: 'You have no tech',
        pain: [
          'No professional email or website',
          'Manual processes for tasks that could be automated',
          'No system for storing customer information',
          'Cobbling together free tools inefficiently',
        ],
        solution: 'Get a computer, internet, microphone, phone, email',
      },
      {
        name: 'Recruiting',
        constraint: "You don't know how to work",
        pain: [
          'Overwhelmed by tasks outside your expertise',
          'Trying to learn everything yourself',
          'No time to execute everything that needs doing',
          'Fear of delegating to someone else',
        ],
        solution: 'You recruit yourself to work',
      },
      {
        name: 'Human Resources',
        constraint: "You're unprotected",
        pain: [
          'No separation between personal and business assets',
          'Legal vulnerability in business dealings',
          'Lack of professional business structure',
          'Personal liability for business activities',
        ],
        solution: 'Create an entity to separate your personal assets from your business assets',
      },
      {
        name: 'Finance',
        constraint: "Your personal money is the business's money",
        pain: [
          'Potential customers have no way to pay you',
          'Relying on inconvenient payment methods',
          'Missing out on impulse purchase opportunities',
          'No professional appearance in financial transactions',
        ],
        solution: 'Get a business bank account. Put money in. Write off start up expenses to save on taxes',
      },
      {
        name: 'Bottom Line',
        constraint: 'Nothing is happening',
        pain: [
          '"Business" remains an expensive hobby',
          'No validation that anyone will pay for your solution',
          'Questioning whether to continue the venture',
          'Feeling like an impostor rather than a business owner',
        ],
        solution: 'Get people to try your stuff for free.',
      },
    ],
    compPackage: {
      structure:
        'Founder Compensation:\n- Personal Savings Depletion: Living expenses funded personally\n- Sweat Equity: 100% ownership via founder shares\n- No formal salary structure exists',
      philosophy:
        'Survival mode. The "compensation" is the equity stake and the learning experience.',
      paymentTrigger: '',
      criticalAction:
        'Inject personal capital ($5-15K) to cover basic business formation costs. Track startup expenses meticulously for future tax write-offs.',
    },
  },

  // ─── Stage 1: Monetize ───
  {
    number: 1,
    name: 'Monetize',
    headcount: '1',
    role: 'Starter',
    leadershipStructure: 'Still just you',
    qtyCompanies: '30,000,000 (100% achieve this level) only 9% own a business',
    departments: [
      {
        name: 'Product',
        constraint: 'Not good enough to sell consistently',
        pain: [
          'Unresolved issues in core functionality',
          'Difficulty articulating product value',
          'Lack of customer validation',
          'Low confidence in asking for payment',
          'Inconsistent product performance',
        ],
        solution: 'Fix it until it\'s good enough to sell. Make v1 product.',
      },
      {
        name: 'Marketing',
        constraint: 'No one knows you have PAID stuff for sale.',
        pain: [
          'Hesitation in promoting paid solutions',
          'Unclear messaging about value proposition',
          'No targeted marketing to potential paying customers',
          'Limited visibility of commercial offerings',
          'No clear distinction between free and paid offerings',
        ],
        solution: 'Tell people you have stuff. Sign up for free ad spend credits.',
      },
      {
        name: 'Sales',
        constraint: "You still don't know how to sell",
        pain: [
          'Inconsistent pitch and value proposition',
          'Difficulty handling pricing objections',
          'Lack of confidence in closing techniques',
          'Uncertainty about what customers will pay for',
          'Anxiety around asking for money',
        ],
        solution: 'Use good results from free people to sell paid people. Offer to solve their problems for money.',
      },
      {
        name: 'Customer Service',
        constraint: "Free customers don't like it or use it",
        pain: [
          "Unclear why people aren't using what you created",
          'No feedback mechanism to understand problems',
          'Free users abandoning without explanation',
          'Frustration over lack of appreciation for free work',
          'Low engagement with your free offering',
        ],
        solution: 'Figure out all hidden cost (why they don\'t like it / use it) by talking to them.',
      },
      {
        name: 'IT',
        constraint: 'You have no basic software / tools or social media.',
        pain: [
          'Using personal accounts for business purposes',
          'No professional email or website',
          'Manual processes for tasks that could be automated',
          'No system for storing customer information',
          'Cobbling together free tools inefficiently',
        ],
        solution: 'Figure out the (free) software you need by joining free groups and reading online forums.',
      },
      {
        name: 'Recruiting',
        constraint: "You don't know how to do something or have the time to do it",
        pain: [
          'Skills gaps preventing progress',
          'Overwhelmed by tasks outside your expertise',
          'Trying to learn everything yourself',
          'No time to execute everything that needs doing',
          'Fear of delegating to someone else',
        ],
        solution: 'Reach out to people you know to help you OR use freelancer platforms to get help.',
      },
      {
        name: 'Human Resources',
        constraint: "You pay money to freelancers to get work done and don't get what you want.",
        pain: [
          'Disappointed with freelancer work quality',
          'No clear agreements on deliverables',
          'Miscommunication about expectations',
          'Wasted money on poor outcomes',
          'Uncertainty about how to manage others',
        ],
        solution: 'Create basic vendor agreements & expectations',
      },
      {
        name: 'Finance',
        constraint: 'You have no way to collect money',
        pain: [
          'Potential customers have no way to pay you',
          'Relying on inconvenient payment methods',
          'Missing out on impulse purchase opportunities',
          'No professional appearance in financial transactions',
          'Unable to accept payments efficiently',
        ],
        solution: 'Set up a payment processor. Run a payment. Get a deposit.',
      },
      {
        name: 'Bottom Line',
        constraint: 'Your business makes no money.',
        pain: [
          '"Business" remains an expensive hobby',
          'No validation that anyone will pay for your solution',
          'Questioning whether to continue the venture',
          'Feeling like an impostor rather than a business owner',
          'Depleting personal savings with no revenue',
        ],
        solution: 'Make your first sale.',
      },
    ],
    compPackage: {
      structure:
        'Founder Compensation:\n- Minimal Draw: $0-$2,000/month (only after first sales)\n- Primary Income: Revenue retained in business\n- Equity: 100% ownership',
      philosophy:
        'Revenue first, compensation second. Every dollar stays in the business to prove viability.',
      paymentTrigger:
        'Only draw personal compensation after achieving $10K+ in cumulative revenue and establishing 3-month operating reserve ($3-5K).',
      criticalAction:
        'Set up payment processor to actually collect money. Open dedicated business bank account. Separate personal and business finances completely.',
    },
  },

  // ─── Stage 2: Advertize ───
  {
    number: 2,
    name: 'Advertize',
    headcount: '1',
    role: 'Doer',
    leadershipStructure: 'Freelancers',
    qtyCompanies: '30,000,000 (100% of companies)',
    departments: [
      {
        name: 'Product',
        constraint: 'Paying customers have higher expectations, product is unreliable & not good enough.',
        pain: [
          'High refund rates or cancellations',
          'Customers pointing out obvious flaws',
          'Core functionality unreliable or incomplete',
          'Embarrassment when demonstrating product',
          'Constantly apologizing for limitations',
        ],
        solution: 'Fix it to be good enough for now, in an "unscalable way"',
      },
      {
        name: 'Marketing',
        constraint: 'Lead flow is inconsistent (STOP: START)',
        pain: [
          'Marketing activities happen only when not busy',
          'Feast-or-famine cycle of leads',
          'No predictable lead generation system',
          'Marketing abandoned during delivery phases',
          'No tracking of which marketing efforts work',
        ],
        solution: 'Live by the Rule of 100. Prioritize advertising daily.',
      },
      {
        name: 'Sales',
        constraint: 'Sales conversions are inconsistent',
        pain: [
          'Success feels random rather than systematic',
          'Different pitch for each prospect',
          'No repeatable process to follow',
          'Anxiety before sales conversations',
          'Inability to predict results from efforts',
        ],
        solution: 'Nail down a script for setting and closing — CLOSER & ACA',
      },
      {
        name: 'Customer Service',
        constraint: 'Paid customers have higher standards & complain more',
        pain: [
          'Shocked by customer expectations after payment',
          'Defensive reactions to feedback',
          'No system for handling complaints',
          'Each customer issue feels personal',
          'Unsure how to turn unhappy customers around',
        ],
        solution: 'Learn to deal with disappointed customer (angry boat). Get testimonials from happy customers (Epiphany script)',
      },
      {
        name: 'IT',
        constraint: "You have no money to afford tech",
        pain: [
          'Using free tools beyond their intended capacity',
          'Trial periods ending on essential software',
          'Cobbling together suboptimal tech solutions',
          'Losing efficiency with inadequate tools',
          'Security concerns with free-tier software',
        ],
        solution: 'Use free tech (Google Suite) and free trials/credits for starter softwares. Website/hosting. Starter CRM / POS',
      },
      {
        name: 'Recruiting',
        constraint: 'Too much work for part timers',
        pain: [
          'Quality inconsistency with temporary workers',
          'Constantly training new part-timers',
          'High turnover of part-time staff',
          'Limited commitment from part-time resources',
          'Unreliable availability of part-time help',
        ],
        solution: 'Convert part timers to full timer, make public post, or run ads on job boards to get full timers',
      },
      {
        name: 'Human Resources',
        constraint: 'You have full timers but structured like contractors.',
        pain: [
          'Limited control over work processes',
          'Complex tax implications',
          'Difficulty building team culture',
          'Legal compliance risks',
          'Potential misclassification liability',
        ],
        solution: 'Set up payroll',
      },
      {
        name: 'Finance',
        constraint: "You don't keep track of money or have a way to pay taxes",
        pain: [
          'Surprise tax obligations',
          'Inability to plan for expenses',
          'Personal and business finances commingled',
          'No structured record-keeping',
          'No visibility into financial performance',
        ],
        solution: 'Spin up QuickBooks equivalent. Save for taxes. Check your bank account daily.',
      },
      {
        name: 'Bottom Line',
        constraint: 'New customers are inconsistent',
        pain: [
          'Difficulty planning based on customer acquisition',
          'Feast-or-famine business cycles',
          'Over-reliance on existing customers',
          'Cash flow challenges',
          'Unpredictable revenue streams',
        ],
        solution: 'Let more people know about your stuff',
      },
    ],
    compPackage: {
      structure:
        'Founder:\nBase Draw: $24-36K annually ($2-3K/month)\nPerformance Bonus: 10% of monthly profit above $5K threshold\nEquity: 100% ownership\n\nPart-time/Freelance Help:\nHourly Rate: Market rate for specific tasks\nPayment: Immediate upon delivery/invoicing\nNo benefits or equity at this stage',
      philosophy:
        "Freelancers get paid immediately; founder gets paid last.",
      paymentTrigger:
        'Monthly Profit Distribution:\n1. Tax Reserve (30%)\n2. Operating Reserve build to $10K\n3. Freelancer payments (immediate)\n4. Founder draw (if reserves adequate)\n5. Reinvestment in marketing',
      criticalAction:
        'Formalize invoicing and payment collection processes. Set up basic bookkeeping. Save 30% of all revenue for taxes immediately.',
    },
  },

  // ─── Stage 3: Stabilize ───
  {
    number: 3,
    name: 'Stabilize',
    headcount: '1 to 4',
    role: 'Trainer',
    leadershipStructure: 'First full-time hires',
    qtyCompanies: '7,140,000 (24% of companies)',
    departments: [
      {
        name: 'Product',
        constraint: 'No time to fix everything that\'s wrong',
        pain: [
          'Multiple issues requiring attention',
          'Customer complaints about various aspects',
          'Technical debt accumulating',
          'Prioritization challenges',
        ],
        solution: 'Fix the thing they complain about most',
      },
      {
        name: 'Marketing',
        constraint: "Leads aren't buying fast enough (colder)",
        pain: [
          'Extended sales cycles',
          'Low conversion rates from initial contact',
          'Prospects requiring more nurturing',
          'Inconsistent follow-up processes',
        ],
        solution: 'Add long term nurture (email &/or content)',
      },
      {
        name: 'Sales',
        constraint: "Wasting time with bad leads. You don't have enough time to talk to good leads.",
        pain: [
          'High number of unqualified prospects',
          'Low conversion rates',
          'Inefficient use of sales time',
          'Difficulty identifying promising opportunities',
        ],
        solution: 'Decide on basic qualifications. Outsource setting & calendaring.',
      },
      {
        name: 'Customer Service',
        constraint: 'New customers feel lost.',
        pain: [
          'Customer confusion after purchase',
          'Frequent basic questions',
          'Slow time-to-value',
          'Risk of early churn',
        ],
        solution: 'Learn to onboard customers properly',
      },
      {
        name: 'IT',
        constraint: "You have a bunch of different tools that you don't use",
        pain: [
          'Tool sprawl',
          'Paying for unused software',
          'Inefficient tech stack',
          'Lack of integration between systems',
        ],
        solution: 'Figure out the useful ones. Extend free trial through credits/discounts. Cut the rest. Basic sales pipeline.',
      },
      {
        name: 'Recruiting',
        constraint: 'Not getting quality applicants.',
        pain: [
          'Unqualified candidates',
          'Poor cultural fit',
          'High turnover',
          'Lengthy hiring process',
        ],
        solution: 'Learn how to write a job description & review resumes.',
      },
      {
        name: 'Human Resources',
        constraint: "You're not withholding employee taxes and fees.",
        pain: [
          'Tax compliance issues',
          'Legal liability',
          'Potential penalties',
          'Administrative challenges',
        ],
        solution: 'Get a payroll provider so all taxes and fees by state are handled.',
      },
      {
        name: 'Finance',
        constraint: 'People getting paid informally',
        pain: [
          'Inconsistent payment processes',
          'Lack of financial records',
          'Difficulty tracking expenses',
          'Tax reporting challenges',
        ],
        solution: 'Setup payroll processing, basic bookkeeping, managing invoices and payments',
      },
      {
        name: 'Bottom Line',
        constraint: "There's too much for one person to do",
        pain: [
          'Owner overwhelm',
          'Bottlenecks in processes',
          'Slow response times',
          'Limited scaling capability',
        ],
        solution: 'Get help.',
      },
    ],
    compPackage: {
      structure:
        'Founder/CEO:\nBase Salary: $40-60K annually\nProfit Distributions: Quarterly, based on formula\nEquity: 80-100% (begin considering structure for future dilution)\n\nFirst Full-Time Hires (1-3):\nBase Salary: 80-85% of market rate\nQuarterly Profit Share: Access to 10% of profit pool\nEquity: None yet (too early for meaningful grants)',
      philosophy:
        "Hire for critical roles at slightly below market; share success through quarterly bonuses.",
      paymentTrigger:
        'Profit-Sharing Pool Activation:\nIF Quarterly Profit < $10K → No profit sharing\nIF Quarterly Profit $10-25K → 5% pool\nIF Quarterly Profit > $25K → 10% pool\n\nPool Distribution:\n- Core Team: 100% split equally among eligible employees\n- Eligibility: Employed full quarter, "meets expectations"',
      criticalAction:
        'Set up formal payroll provider. Create basic employee handbook. Draft simple profit-sharing agreement.',
    },
  },

  // ─── Stage 4: PRIORITIZE ───
  {
    number: 4,
    name: 'PRIORITIZE',
    headcount: '5 to 9',
    role: 'Manager',
    leadershipStructure: '1 layer: First Team',
    qtyCompanies: '3,000,000',
    departments: [
      {
        name: 'Product',
        constraint: 'You said yes to anyone who would pay. Feedback from so many different customers creates too many product directions.',
        pain: [
          'Product roadmap pulled in multiple directions',
          'Feature bloat without strategic focus',
          'Inability to satisfy diverse customer needs',
          'Competing priorities from different customer segments',
          'Development resources spread too thin',
        ],
        solution: 'Specialize product (V2) — price to serve niched down avatar',
      },
      {
        name: 'Marketing',
        constraint: 'Too many unqualified leads',
        pain: [
          'Marketing attracts poor-fit prospects',
          'High volume but low conversion rate',
          'Resources wasted on unsuitable leads',
          'Unclear messaging attracting the wrong audience',
          'Difficulty identifying ideal customers',
        ],
        solution: 'Make better free stuff & more creative to boost volume. Add qualification / friction so extra volume self-selects.',
      },
      {
        name: 'Sales',
        constraint: 'Speed to contact drops. You have no metrics around sales.',
        pain: [
          'Delayed follow-up with prospects',
          'No visibility into sales performance',
          'Inability to identify process bottlenecks',
          'No accountability for sales outcomes',
          'Guessing at what works vs. what doesn\'t',
        ],
        solution: 'Track basic KPIs manually on spreadsheet (Contact rate, sched, show, offer, close, cash collected)',
      },
      {
        name: 'Customer Service',
        constraint: 'No metrics for customer success.',
        pain: [
          'Fragmented customer information',
          'Inconsistent service quality',
          'No visibility into customer health',
          'Difficulty identifying at-risk customers',
        ],
        solution: 'Install tracking & KPI. Record and centralize notes for each customer in a CRM',
      },
      {
        name: 'IT',
        constraint: "New team isn't using everything",
        pain: [
          'Inconsistent tool usage across team',
          'Security vulnerabilities from poor password management',
          'Inefficient communication and project tracking',
          'Siloed information',
        ],
        solution: 'Get everyone on platforms. Protect passwords. Get project management tool & comms (work chat tool)',
      },
      {
        name: 'Recruiting',
        constraint: 'Wasting time with bad candidates.',
        pain: [
          'Poor hiring decisions',
          'Ineffective interview process',
          'Inadequate vetting of candidates',
          'High early turnover',
          'Hiring based on gut feeling rather than evidence',
        ],
        solution: 'Learn how to conduct a proper interview and check references.',
      },
      {
        name: 'Human Resources',
        constraint: "Employees don't know how to behave (PTO, sick days, dress code, code of conduct)",
        pain: [
          'Inconsistent expectations around workplace norms',
          'Ad-hoc management of time off',
          'Confusion around appropriate workplace behavior',
          'Potential compliance issues',
          'Inconsistent treatment of team members',
        ],
        solution: 'Employee handbook & policies.',
      },
      {
        name: 'Finance',
        constraint: 'Cashflow is lumpy due to random "one-time" expenses. You find out that businesses cost money to run.',
        pain: [
          'Unexpected expenses creating cash crunches',
          'Lack of financial planning',
          'No visibility into true cost of operations',
          'Reactive rather than proactive financial management',
          'Potential liability exposure',
        ],
        solution: 'Get profit and loss statements and cash flow statements setup. Get basic business insurance.',
      },
      {
        name: 'Bottom Line',
        constraint: 'Trying to be everything to everyone',
        pain: [
          'Diffused focus across too many customer types',
          'Inability to deeply serve any market segment',
          'Resource inefficiency from serving diverse needs',
          "Marketing that doesn't resonate strongly with anyone",
          'No clear competitive advantage',
        ],
        solution: 'Niche down to serve only people like your best customers. Pick your ideal customers. Say no to others. Set up basic systems. Create clear rules. Measure what matters.',
      },
    ],
    compPackage: {
      structure:
        'Founder/CEO:\nBase Salary: $60-80K annually\nQuarterly Bonus: 15% of quarterly profit (personal)\nEquity: 70-80% (begin dilution planning)\nDividends: Residual after team compensation and reserves\n\nFirst Team/Managers (Directors):\nBase Salary: 85-95% of market rate\nQuarterly Bonus: 15-20% of base tied to:\n  - Company quarterly profit target (50%)\n  - Department KPIs (50%)\nPhantom Shares: 0.5-1% profit interest, 3-year vest',
      philosophy:
        'Build management layer with performance-based compensation; introduce phantom equity for retention.',
      paymentTrigger:
        'Define Ideal Customer Profile — compensation follows focus. Implement basic CRM for sales tracking. Create departmental KPI scorecards.',
      criticalAction: '',
    },
  },

  // ─── Stage 5: PRODUCTIZE ───
  {
    number: 5,
    name: 'PRODUCTIZE',
    headcount: '10 to 19',
    role: 'Director',
    leadershipStructure: '1.5 layer: First 2-3 Managers',
    qtyCompanies: '2,400,000',
    departments: [
      {
        name: 'Product',
        constraint: 'Customers have nothing else to buy from you & churn',
        pain: [
          'High customer churn after initial purchase',
          'Limited lifetime value from one-time buyers',
          'No revenue expansion opportunities',
          'Customer acquisition costs not fully recovered',
          'Difficulty maintaining customer engagement long-term',
        ],
        solution: 'Connect CS & product. Make something new to sell them.',
      },
      {
        name: 'Marketing',
        constraint: 'Qualified leads are too expensive and cap your ability to advertise',
        pain: [
          'CAC approaching or exceeding profitability threshold',
          'Limited ad spend due to high acquisition costs',
          'Inefficient lead qualification process',
          'Inability to scale marketing efforts',
          'Difficulty identifying most valuable prospects',
        ],
        solution: 'Spend more on niche down messaging with new offer & raise CAC limit (spend more). Score your leads (you select). Attribution + CRO.',
      },
      {
        name: 'Sales',
        constraint: 'Customers get sold with unrealistic expectations and refunds/bad reviews become an issue',
        pain: [
          'High refund rates from misaligned expectations',
          'Negative reviews hurting reputation',
          'Inconsistent sales messaging',
          'Lack of alignment between sales promises and delivery',
          'No quality control on sales conversations',
        ],
        solution: 'Create sales aids/material. Install weekly game tape between CS & sales/marketing for QA & feedback. Track NPS/CSAT/Refund rate by rep.',
      },
      {
        name: 'Customer Service',
        constraint: 'Customers come in with unrealistic expectations. Get inconsistent answers.',
        pain: [
          'Support team giving contradictory information',
          'No standardized response process',
          'Customer confusion from inconsistent messaging',
          'High support ticket volume from preventable issues',
          'Limited customer progression beyond initial purchase',
        ],
        solution: 'Build proper CS playbook to onboard & train team to answer Qs same way. Weekly game tape review. Customer ascension path.',
      },
      {
        name: 'IT',
        constraint: 'Employees using their personal hardware and software and creates issues when people leave. No tracking for leads and customers interacting with your business.',
        pain: [
          'Loss of data when employees depart',
          'Security vulnerabilities from personal devices',
          'Inconsistent software versions and capabilities',
          'Limited visibility into customer interactions',
          'Difficulty tracking customer journey',
        ],
        solution: 'Company provides company owned/controlled hardware and software. Need dashboard / reporting system.',
      },
      {
        name: 'Recruiting',
        constraint: 'Have trouble finding higher level roles/managers',
        pain: [
          'Leadership gaps slowing growth',
          'Over-reliance on founder/executive team',
          'Difficulty attracting experienced managers',
          'Failed external hires due to culture mismatch',
          'Hiring process not suited for management roles',
        ],
        solution: 'Deliberate promotions from within rather than Peter Principle. Incentivize employee referrals = CAT = cost to acquire talent.',
      },
      {
        name: 'Human Resources',
        constraint: "You're firing people incorrectly and exposed",
        pain: [
          'Legal liability from improper terminations',
          'Inconsistent handling of performance issues',
          'No documentation of termination reasons',
          'Poor offboarding procedures',
          'Risk of wrongful termination claims',
        ],
        solution: 'Create termination policies and process',
      },
      {
        name: 'Finance',
        constraint: "You don't know how much money you can reinvest into growth",
        pain: [
          'Uncertainty about reinvestment capacity',
          'Reactive spending decisions',
          'Cash flow surprises',
          'Missed tax optimization opportunities',
          'Difficulty planning for major expenses',
        ],
        solution: 'Make Budget (save for big expenses), simple financial forecasting, begin keeping track of tax write offs.',
      },
      {
        name: 'Bottom Line',
        constraint: 'Not making enough per customer. Low LTV:CAC',
        pain: [
          'Customer acquisition costs not justified by lifetime value',
          'Growth limited by economics of customer acquisition',
          'Single-purchase business model limitations',
          'Revenue plateaus despite customer growth',
          'Difficulty funding continued marketing',
        ],
        solution: 'Make and sell your second premium product to customers.',
      },
    ],
    compPackage: {
      structure: '',
      philosophy:
        'Compensation explicitly rewards increasing customer lifetime value and developing premium offerings.',
      paymentTrigger: 'Basic health/dental/vision benefits. Company retirement plan (401k/RRSP) with 3-5% match. Performance bonuses replace pure profit sharing.',
      criticalAction: '',
    },
  },

  // ─── Stage 6: OPTIMIZE ───
  {
    number: 6,
    name: 'OPTIMIZE',
    headcount: '20 to 49',
    role: 'Leader',
    leadershipStructure: '2 layers: Full Team of Managers',
    qtyCompanies: '2,100,000',
    departments: [
      {
        name: 'Product',
        constraint: 'Improving two products slows down improvements.',
        pain: [
          'Slower iteration cycles on each product',
          'Competing priorities creating bottlenecks',
          'Diminishing returns on development efforts',
          'Inconsistent product improvement velocity',
          'Development resources stretched thin across products',
        ],
        solution: 'Install incremental product improvement process for both products.',
      },
      {
        name: 'Marketing',
        constraint: "Ads don't convert as well to colder traffic. CAC goes up.",
        pain: [
          'Saturation in warm audience segments',
          'Diminishing returns on existing ad creative',
          'Difficulty scaling ad spend profitably',
          'Marketing efficiency declining with growth',
          'Increasing cost to acquire customers',
        ],
        solution: 'Implement Ad assembly process to increase volume.',
      },
      {
        name: 'Sales',
        constraint: 'Closing efficiency on core product drops. Becomes inconsistent. Refunds go down but so do closes.',
        pain: [
          'Lower conversion rates despite quality improvements',
          'Variable skill levels across sales team',
          'Lack of systematic sales approach',
          'Difficulty identifying performance improvement areas',
          'Inconsistent sales performance across team',
        ],
        solution: 'Install sales training system, individual coaching, and team cadence.',
      },
      {
        name: 'Customer Service',
        constraint: 'Churn increases due to volume & new product.',
        pain: [
          'Inconsistent customer experience across segments',
          'Difficulty identifying at-risk customers',
          'Varying product adoption and usage patterns',
          'Inability to prioritize retention efforts effectively',
          'Rising customer attrition rates',
        ],
        solution: 'Create customer segmentation by cohort and activation points.',
      },
      {
        name: 'IT',
        constraint: 'New people coming in. Old people leaving with IP, data and access.',
        pain: [
          'Intellectual property protection challenges',
          'Inconsistent system access protocols',
          'Data exposure risks during transitions',
          'Inefficient onboarding of new team members',
          'Security vulnerabilities from former employees',
        ],
        solution: 'Cybersecurity, Malware, Password protection from and for employees. Onboarding and offboarding employees.',
      },
      {
        name: 'Recruiting',
        constraint: 'The number and type of roles you need exceed your current network. (Everyone who\'s good has a job already)',
        pain: [
          'Difficulty finding specialized skills',
          'Competition for experienced candidates',
          'Ineffective screening of large candidate pools',
          'Long time-to-fill for critical positions',
          'Limited talent pool from existing networks',
        ],
        solution: 'You learn to headhunt higher level roles. Add multiple interviews filtering for different skills.',
      },
      {
        name: 'Human Resources',
        constraint: "You can't get higher level talent because they expect full compensation package (benefits, retirement, etc)",
        pain: [
          'Compensation package not competitive',
          'Limited benefits offerings deterring candidates',
          'High turnover of senior staff',
          'Challenges retaining top performers',
          'Inability to attract executive talent',
        ],
        solution: 'Setup benefits, retirement and advanced compensation plans',
      },
      {
        name: 'Finance',
        constraint: "You're spending money to grow, but you're not. Money is missing. Where's my return?",
        pain: [
          'Lack of visibility into financial performance',
          'Inefficient capital allocation',
          'Cash flow management challenges',
          'Inadequate financial controls',
        ],
        solution: 'Scale up AR/AP. Get more granular financial data. Balance Sheet. Begin making quarterly tax payments.',
      },
      {
        name: 'Bottom Line',
        constraint: 'Everything is inefficient.',
        pain: [
          'Multiple operational bottlenecks',
          'Complexity increasing faster than capabilities',
          'Increasing costs without proportional revenue growth',
          'Systems not scaling with company size',
        ],
        solution: 'Grow by doing everything better rather than more.',
      },
    ],
    compPackage: {
      structure: '',
      philosophy:
        'Reward operational excellence and process improvement; transition from quarterly to annual performance cycles for leadership.',
      paymentTrigger: '',
      criticalAction:
        'Document SOPs for bottlenecked processes. Implement sales training system. Create customer segmentation models. Scale AR/AP processes.',
    },
  },

  // ─── Stage 7: CATEGORIZE ───
  {
    number: 7,
    name: 'CATEGORIZE',
    headcount: '50 to 99',
    role: 'Executive',
    leadershipStructure: '3 Layers: Leadership team',
    qtyCompanies: '1,200,000',
    departments: [
      {
        name: 'Product',
        constraint: 'Product outdated. Improvements take too long to roll out.',
        pain: [
          'Feature release cycles too slow',
          'Difficulty prioritizing improvements',
          'Development resources spread too thin',
          'Inability to address customer needs quickly',
          'Product falling behind market standards',
        ],
        solution: 'Fix one problem at a time with focused feature specific teams.',
      },
      {
        name: 'Marketing',
        constraint: 'Lead flow is volatile because you only have one way to get customers.',
        pain: [
          'Inconsistent lead generation',
          'Overreliance on single acquisition source',
          'Limited audience diversity',
          'No redundancy in marketing strategy',
          'Vulnerability to channel disruptions',
        ],
        solution: 'Fund 2nd acquisition channel with referral process (& track it) & segmented LT nurture by lead score. Pick most similar platform → or new core 4.',
      },
      {
        name: 'Sales',
        constraint: 'Leads getting wasted. Not worked consistently.',
        pain: [
          'Inconsistent follow-up processes',
          'Coverage gaps in sales team',
          'Variation in lead handling quality',
          'Poor show rates for scheduled appointments',
          'Leads falling through cracks',
        ],
        solution: 'Better schedule coverage. Create lead nurture checklist & kudos for top show rates.',
      },
      {
        name: 'Customer Service',
        constraint: 'CS only reactive. All firefighting.',
        pain: [
          'High support ticket volume',
          'No strategic customer management',
          'Missed revenue opportunities with existing customers',
          'Customer relationship limited to problem-solving',
          'Always responding to problems, never preventing them',
        ],
        solution: 'Create proactive customer journey & communication. Focus on renewals and ascensions.',
      },
      {
        name: 'IT',
        constraint: 'Data, IP, Access all over the place and disorganized.',
        pain: [
          'Inconsistent access controls',
          'Intellectual property protection risks',
          'Inefficient information retrieval',
          'System vulnerability due to fragmentation',
          'Decentralized and unsecured data storage',
        ],
        solution: 'Network technology. Bring solution to the cloud. Data host. Centrally locate hardware if onsite.',
      },
      {
        name: 'Recruiting',
        constraint: "Takes too long to hire good people and they get hired elsewhere. You have too many roles open to keep track of candidates and where they're at in the process.",
        pain: [
          'Extended time-to-fill for critical positions',
          'Disorganized candidate tracking',
          'Inconsistent interview processes',
          'Inability to manage multiple open positions effectively',
          'Losing top candidates to competitors',
        ],
        solution: 'Install Applicant Tracking System to keep track of so many open roles. Pay specialized recruiting firms to keep up.',
      },
      {
        name: 'Human Resources',
        constraint: 'There are too many different places for employee information and you have no data.',
        pain: [
          'Inconsistent HR data management',
          'Difficulty accessing consolidated employee information',
          'Limited ability to analyze workforce trends',
          'Compliance risks from scattered information',
          'Fragmented employee records',
        ],
        solution: 'Setup HRIS - Human Resource Information System',
      },
      {
        name: 'Finance',
        constraint: 'Your team starts spending your money stupidly.',
        pain: [
          'Lack of financial accountability',
          'No approval process for expenses',
          'Difficulty tracking department-specific ROI',
          'Growing financial waste',
          'Uncontrolled departmental spending',
        ],
        solution: 'Quarterly expense management. Budgets by dept. Cards with limits. Expense approval process. Add insurance coverage & get fancier projections.',
      },
      {
        name: 'Bottom Line',
        constraint: 'Every system is overwhelmed. The business is disorganized.',
        pain: [
          'Crisis management instead of strategic leadership',
          'Difficulty prioritizing among competing problems',
          'Organizational complexity exceeding management capacity',
          'Growth creating more problems than solutions',
          'Multiple operational bottlenecks simultaneously',
        ],
        solution: 'Triage appropriately',
      },
    ],
    compPackage: {
      structure: '',
      philosophy:
        'Segment-specific compensation recognizes specialized contributions; ownership distributed to functional leaders.',
      paymentTrigger: '',
      criticalAction: '',
    },
  },

  // ─── Stage 8: SPECIALIZE ───
  {
    number: 8,
    name: 'SPECIALIZE',
    headcount: '100 to 249',
    role: 'President',
    leadershipStructure: '4 layers: Executive',
    qtyCompanies: '900,000',
    departments: [
      {
        name: 'Product',
        constraint: 'Product has too many features & overwhelms customers',
        pain: [
          'Low adoption of many features',
          'Increased support needs due to complexity',
          'Difficulty onboarding new users',
          'Diluted core value proposition',
          'Feature bloat causing user confusion',
        ],
        solution: 'Survey. Prune. Improve through deletion. Pruned items can become cross-sells.',
      },
      {
        name: 'Marketing',
        constraint: 'Broader creative brings in lower quality leads and dilutes the brand. CAC goes up again.',
        pain: [
          'Inability to accurately measure marketing ROI',
          'Growing regulatory scrutiny of marketing practices',
          'Platform policy changes threatening acquisition channels',
          'Difficulty optimizing marketing spend across channels',
          'Complex customer journeys spanning multiple channels',
        ],
        solution: 'Grow brand with standardized messaging + free value + big positive brand associations.',
      },
      {
        name: 'Sales',
        constraint: 'Closing efficiency on premium product drops.',
        pain: [
          'Inefficient allocation of sales talent',
          'Lack of visibility into performance metrics',
          'Top performers handling low-value opportunities',
          'Inefficient lead routing',
          'Inconsistent performance on high-value offerings',
        ],
        solution: 'Route best leads to best closers (front end & ascensions). Automated metrics tracking.',
      },
      {
        name: 'Customer Service',
        constraint: 'Multiple customer journeys conflict or are poorly timed with one another.',
        pain: [
          'Inconsistent timing of customer touchpoints',
          'Missed cross-selling opportunities',
          'Siloed customer engagement strategies',
          'Poor coordination between product lines',
          'Customer confusion from overlapping communications',
        ],
        solution: 'Create proactive recommendations to cross-sell other stuff and a blended customer journey.',
      },
      {
        name: 'IT',
        constraint: 'Departments outgrow general software and need specific solutions. Too many internal inquiries. No inquiry process. Departmental policies.',
        pain: [
          'IT overwhelmed with support requests',
          'No prioritization system for technical issues',
          'Inconsistent service levels across departments',
          'Lack of clear technology governance',
          'Generic tools no longer meeting specialized needs',
        ],
        solution: 'Get specialized stack for departments. Internal inquiry process. Staff up if needed. Create departmental policies and service level agreements.',
      },
      {
        name: 'Recruiting',
        constraint: 'Recruiters hold inconsistent standards and culture suffers. Wrong candidates get too far in the process.',
        pain: [
          'Inconsistent candidate assessment',
          'High turnover from misaligned hires',
          'Inefficient interview process',
          "Loss of founder's influence on hiring decisions",
          'Cultural dilution from poor hiring choices',
        ],
        solution: 'Standardized selection process for all incoming talent. Founder final check.',
      },
      {
        name: 'Human Resources',
        constraint: 'So many new and existing employees, performance suffers. Compliance issues and complaints skyrocket.',
        pain: [
          'Rising HR compliance problems',
          'Increase in employee complaints',
          'Diluted company culture',
          'Ineffective onboarding of new hires',
          'Inconsistent employee performance',
        ],
        solution: 'Setup Group Onboarding (Software). Documented performance management systems. Insurance (EPLI). Reinforce cultural values.',
      },
      {
        name: 'Finance',
        constraint: "You're still paying retail for everything (and getting overcharged). Cash is unprotected & getting 0% returns.",
        pain: [
          'Cash management inefficiencies',
          'Money left on the table in negotiations',
          'Inadequate financial controls',
          'Tax optimization opportunities missed',
          'No economies of scale in purchasing',
        ],
        solution: 'Re-negotiate rates on everything. Cash to yield accounts. Specialize finance roles (especially tax mitigation). Internal audits cadence.',
      },
      {
        name: 'Bottom Line',
        constraint: 'No one is good enough to know everything',
        pain: [
          'Decision quality suffering from breadth over depth',
          'Leaders stretched across too many domains',
          'Inability to develop specialized capabilities',
          'Competitive disadvantage against focused competitors',
          'Generalist approach limiting expertise',
        ],
        solution: 'Create dedicated people and teams for specific tasks.',
      },
    ],
    compPackage: {
      structure: '',
      philosophy:
        'Pay premium for specialized expertise that creates competitive moats; use long-term equity to retain critical talent.',
      paymentTrigger: '',
      criticalAction: '',
    },
  },

  // ─── Stage 9: CAPITALIZE ───
  {
    number: 9,
    name: 'CAPITALIZE',
    headcount: '250 to 500',
    role: 'Chairman',
    leadershipStructure: "5 Layers: CEOs",
    qtyCompanies: '300,000',
    departments: [
      {
        name: 'Product',
        constraint: 'Changes to successful products (cash cow) make them worse. But you need new stuff to keep growing.',
        pain: [
          'Innovation stagnation in mature products',
          'Growth ceiling with existing product suite',
          'Difficulty balancing maintenance vs innovation',
          'Market saturation of core offerings',
          'Risk of damaging established revenue streams',
        ],
        solution: 'Buy (M&A) or build (R&D) next rising star product to add to the suite (1+ year out)',
      },
      {
        name: 'Marketing',
        constraint: 'Lead attribution by channel becomes difficult. Platform and regulatory compliance become an issue.',
        pain: [
          'Inability to accurately measure marketing ROI',
          'Growing regulatory scrutiny of marketing practices',
          'Platform policy changes threatening acquisition channels',
          'Difficulty optimizing marketing spend across channels',
          'Complex customer journeys spanning multiple channels',
        ],
        solution: 'Shift to brand first advertising with omnichannel approach with blended ROAS across all products. Install compliance process.',
      },
      {
        name: 'Sales',
        constraint: 'Too many different leads and products for any one type of salesperson.',
        pain: [
          'Inconsistent expertise across product lines',
          'Inefficient lead routing and qualification',
          'Territory management challenges',
          'Difficulty developing specialized sales expertise',
          'Sales team struggling with complex product portfolio',
        ],
        solution: 'Salespeople specialize in selling different products to different leads using different processes. Route lead by product/territory.',
      },
      {
        name: 'Customer Service',
        constraint: 'Support for varied newer offerings is breaking due to high volume.',
        pain: [
          'Extended resolution times for specialized issues',
          'Inconsistent service quality across product lines',
          'Long training cycles for support personnel',
          'Customer dissatisfaction with support experience',
          'Support team overwhelmed by product diversity',
        ],
        solution: 'Triage support to specialized CS rep to improve efficiency and shorten training.',
      },
      {
        name: 'IT',
        constraint: 'Old tech stack insufficient and needs update. Departments demand new & specialized solutions.',
        pain: [
          'Department-specific technology needs unmet',
          'Integration challenges between disparate systems',
          'Security risks in outdated architecture',
          'Infrastructure limiting business flexibility',
          'Difficulty attracting executive talent',
        ],
        solution: 'You hire more specialized tech roles for each major tech function with updated and segmented tech stack. And you may need a new enterprise level software.',
      },
      {
        name: 'Recruiting',
        constraint: "Best talent doesn't respond to standard recruiting efforts from anyone besides CEO/Founder",
        pain: [
          'Extended hiring cycles for key positions',
          'Reluctance of top candidates to engage',
          'Competitive disadvantage in talent market',
          'Vision and growth story not effectively communicated',
          'Difficulty attracting executive talent',
        ],
        solution: 'Founder directly attracts highest level roles and sells on growth/vision. (6+ month cycle to feel comfortable)',
      },
      {
        name: 'Human Resources',
        constraint: "Churn increases + you don't have who you need to grow",
        pain: [
          'Skill gaps limiting strategic initiatives',
          'Reactive rather than proactive talent management',
          'Organizational structure limitations',
          'Over-reliance on key individuals',
        ],
        solution: 'Strategic workforce planning: org, capacity planning, backfill, skill gaps, role redundancy, from generalists to hyper specialization.',
      },
      {
        name: 'Finance',
        constraint: "Your finances aren't legit enough to get other people to give you money (raise/borrow/buy) to make a big investment M&A or R&D.",
        pain: [
          'Financial records insufficient for due diligence',
          'Capital structure not optimized for external investment',
          'Unable to fund strategic acquisitions',
          'Financial processes not robust enough for third-party scrutiny',
          'Limited access to growth capital',
        ],
        solution: '"Prep for Sale": Get QofE. Banker Audit. Capital structure. Hedge big investments. Optional: Raise cash for M&A or to fuel growth.',
      },
      {
        name: 'Bottom Line',
        constraint: 'Not sure where the next big growth comes from.',
        pain: [
          'Diminishing returns on incremental improvements',
          'Uncertainty about future market direction',
          'Competitors closing competitive gaps',
          'Pressure to find new growth vectors',
          'Organic growth slowing in core business',
        ],
        solution: 'Make a big bet on the future.',
      },
    ],
    compPackage: {
      structure:
        'Chairman/CEO:\nBase Salary: $250-400K\nAnnual Bonus: 50-100% tied to transformative goals\nEquity: 20-30% (significant dilution from earlier stages)\nTransaction Bonuses: Tied to M&A success, funding rounds\nBoard Compensation: If transitioning to Chairman role\n\nBusiness Unit Presidents/Division CEOs:\nBase: $150-250K\nAnnual Bonus: 40-80% of base\nEquity: 5-10% of division or company\nCarried Interest: Direct participation in BU performance\nRetention Bonuses: Multi-year golden handcuffs ($500K-$2M)\nTransaction Success Fees: % of acquisition integration success\n\nC-Suite:\nBase: $150-300K\nAnnual Bonus: 35-75% of base\nEquity: 2-7% of company\nPerformance Equity: Vests on IPO, sale, or revenue milestones\nClawback Provisions: For financial restatements',
      philosophy:
        'Compensation becomes sophisticated enough to compete for Fortune 500 talent; equity participates in transformative value creation; align incentives for exit events.',
      paymentTrigger: '',
      criticalAction:
        'Conduct formal Quality of Earnings (QofE) analysis. Engage auditors for "Banker Audit". Optimize capital structure for external events. Consider raising significant cash for M&A. Implement advanced financial controls for third-party scrutiny. Establish compensation committee of board.',
    },
  },
];
